import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Typography, Container, Paper, Button, TextField, Grid, Alert,
  RadioGroup, FormControlLabel, Radio, FormControl, FormLabel,
  Switch, Divider, Box, IconButton, MenuItem, Select, InputLabel,
} from '@mui/material';
import { Save, AddCircleOutline, DeleteOutline } from '@mui/icons-material';
import { homePageSettingsService } from '../../services/api';
import TopBar from '../../components/TopBar';

const ICON_OPTIONS = [
  'LocalHospital', 'EventAvailable', 'MedicalServices', 'Science', 'Favorite',
  'ChildCare', 'Visibility', 'Hearing', 'Psychology', 'AccessibilityNew',
  'PregnantWoman', 'Biotech', 'EmergencyShare', 'VerifiedUser', 'Groups',
  'Schedule', 'Star',
];

const SITE_MODE_PRESETS = {
  Hospital: {
    heroHeadline: 'Your Health, Our Commitment',
    heroSubtext: 'Compassionate, evidence-based care delivered by a team of specialists — all under one roof.',
    showDoctorsSection: true,
    showDemoBanner: false,
  },
  Doctor: {
    heroHeadline: 'Personalised Care, Every Step of the Way',
    heroSubtext: 'One-on-one consultations with a dedicated specialist you can trust.',
    showDoctorsSection: false,
    showDemoBanner: false,
  },
  Demo: {
    heroHeadline: 'See the Platform in Action',
    heroSubtext: 'This is a demo instance showcasing the hospital management system.',
    showDoctorsSection: true,
    showDemoBanner: true,
  },
};

const emptyForm = {
  siteMode: 'Hospital',
  brandName: '',
  tagline: '',
  logoUrl: '',
  primaryColor: '#0d47a1',
  establishedYear: '',
  heroHeadline: '',
  heroSubtext: '',
  aboutText: '',
  phone1: '',
  phone2: '',
  email: '',
  address: '',
  workingHours: '',
  footerTagline: '',
  showDoctorsSection: true,
  showDemoBanner: false,
  demoBannerText: '',
};

// Generic editor for the repeatable content blocks (stats / specialities / services / why-us)
const RepeatableListEditor = ({ title, items, setItems, fields, addLabel }) => {
  const addItem = () => {
    const blank = {};
    fields.forEach((f) => { blank[f.name] = f.type === 'icon' ? ICON_OPTIONS[0] : ''; });
    setItems([...items, blank]);
  };
  const updateItem = (idx, name, value) => {
    const next = items.slice();
    next[idx] = { ...next[idx], [name]: value };
    setItems(next);
  };
  const removeItem = (idx) => setItems(items.filter((_, i) => i !== idx));

  return (
    <Box sx={{ mb: 1 }}>
      <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1.5 }}>{title}</Typography>
      {items.map((item, idx) => (
        <Box key={idx} sx={{ display: 'flex', gap: 1.5, alignItems: 'center', mb: 1.5, flexWrap: 'wrap' }}>
          {fields.map((f) => (
            f.type === 'icon' ? (
              <FormControl key={f.name} size="small" sx={{ minWidth: 160 }}>
                <InputLabel>{f.label}</InputLabel>
                <Select label={f.label} value={item[f.name] || ICON_OPTIONS[0]} onChange={(e) => updateItem(idx, f.name, e.target.value)}>
                  {ICON_OPTIONS.map((opt) => <MenuItem key={opt} value={opt}>{opt}</MenuItem>)}
                </Select>
              </FormControl>
            ) : f.type === 'color' ? (
              <TextField key={f.name} size="small" label={f.label} type="color" value={item[f.name] || '#1976d2'} onChange={(e) => updateItem(idx, f.name, e.target.value)} sx={{ width: 90 }} />
            ) : (
              <TextField
                key={f.name}
                size="small"
                label={f.label}
                value={item[f.name] || ''}
                onChange={(e) => updateItem(idx, f.name, e.target.value)}
                multiline={f.type === 'textarea'}
                sx={{ minWidth: f.type === 'textarea' ? 280 : 160, flexGrow: f.type === 'textarea' ? 1 : 0 }}
              />
            )
          ))}
          <IconButton color="error" onClick={() => removeItem(idx)} size="small"><DeleteOutline /></IconButton>
        </Box>
      ))}
      <Button size="small" startIcon={<AddCircleOutline />} onClick={addItem}>{addLabel}</Button>
    </Box>
  );
};

const HomePageSettingsManagement = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState(emptyForm);
  const [stats, setStats] = useState([]);
  const [specialities, setSpecialities] = useState([]);
  const [services, setServices] = useState([]);
  const [whyUs, setWhyUs] = useState([]);
  const [success, setSuccess] = useState('');

  useEffect(() => { loadSettings(); }, []);

  const safeParse = (json, fallback) => {
    try { return json ? JSON.parse(json) : fallback; } catch { return fallback; }
  };

  const loadSettings = async () => {
    try {
      const response = await homePageSettingsService.getSettings();
      const d = response.data;
      setFormData({
        siteMode: d.siteMode || 'Hospital',
        brandName: d.brandName || '',
        tagline: d.tagline || '',
        logoUrl: d.logoUrl || '',
        primaryColor: d.primaryColor || '#0d47a1',
        establishedYear: d.establishedYear || '',
        heroHeadline: d.heroHeadline || '',
        heroSubtext: d.heroSubtext || '',
        aboutText: d.aboutText || '',
        phone1: d.phone1 || '',
        phone2: d.phone2 || '',
        email: d.email || '',
        address: d.address || '',
        workingHours: d.workingHours || '',
        footerTagline: d.footerTagline || '',
        showDoctorsSection: d.showDoctorsSection ?? true,
        showDemoBanner: d.showDemoBanner ?? false,
        demoBannerText: d.demoBannerText || '',
      });
      setStats(safeParse(d.statsJson, []));
      setSpecialities(safeParse(d.specialitiesJson, []));
      setServices(safeParse(d.servicesJson, []));
      setWhyUs(safeParse(d.whyUsJson, []));
    } catch (err) {
      console.error('Failed to load home page settings');
    }
  };

  const applyPreset = (mode) => {
    const preset = SITE_MODE_PRESETS[mode];
    setFormData((prev) => ({
      ...prev,
      siteMode: mode,
      showDoctorsSection: preset.showDoctorsSection,
      showDemoBanner: preset.showDemoBanner,
      heroHeadline: prev.heroHeadline || preset.heroHeadline,
      heroSubtext: prev.heroSubtext || preset.heroSubtext,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await homePageSettingsService.updateSettings({
        ...formData,
        statsJson: JSON.stringify(stats),
        specialitiesJson: JSON.stringify(specialities),
        servicesJson: JSON.stringify(services),
        whyUsJson: JSON.stringify(whyUs),
      });
      setSuccess('Home page settings updated successfully!');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      console.error('Failed to update home page settings');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
      <TopBar title="Home Page Settings" onBack={() => navigate('/dashboard')} />
      <Container maxWidth="md" sx={{ py: 4 }}>
        {success && <Alert severity="success" sx={{ mb: 3 }}>{success}</Alert>}
        <Paper elevation={3} sx={{ p: 4, borderRadius: 2 }}>
          <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, mb: 1 }}>Customise Public Website</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Configure how the public homepage (vedantahospital.com style landing page) looks for this deployment — as a multi-doctor Hospital, a single Doctor's personal site, or a Demo showcase.
          </Typography>
          <form onSubmit={handleSubmit}>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <FormControl component="fieldset">
                  <FormLabel component="legend" sx={{ fontWeight: 600, color: 'text.primary', mb: 0.5 }}>Site Mode</FormLabel>
                  <RadioGroup row value={formData.siteMode} onChange={(e) => applyPreset(e.target.value)}>
                    <FormControlLabel value="Hospital" control={<Radio />} label="Hospital (multi-doctor)" />
                    <FormControlLabel value="Doctor" control={<Radio />} label="Specific Doctor (solo practice)" />
                    <FormControlLabel value="Demo" control={<Radio />} label="Demo Site" />
                  </RadioGroup>
                </FormControl>
              </Grid>

              <Grid item xs={12}><Divider /></Grid>
              <Grid item xs={12}><Typography variant="subtitle1" sx={{ fontWeight: 600 }}>Branding</Typography></Grid>
              <Grid item xs={12} sm={6}><TextField fullWidth label="Brand / Hospital / Doctor Name" value={formData.brandName} onChange={(e) => setFormData({ ...formData, brandName: e.target.value })} /></Grid>
              <Grid item xs={12} sm={6}><TextField fullWidth label="Tagline" value={formData.tagline} onChange={(e) => setFormData({ ...formData, tagline: e.target.value })} /></Grid>
              <Grid item xs={12} sm={6}><TextField fullWidth label="Logo URL (optional — upload via Media Management)" value={formData.logoUrl} onChange={(e) => setFormData({ ...formData, logoUrl: e.target.value })} /></Grid>
              <Grid item xs={6} sm={3}><TextField fullWidth label="Primary Color" type="color" value={formData.primaryColor} onChange={(e) => setFormData({ ...formData, primaryColor: e.target.value })} /></Grid>
              <Grid item xs={6} sm={3}><TextField fullWidth label="Established Year" value={formData.establishedYear} onChange={(e) => setFormData({ ...formData, establishedYear: e.target.value })} /></Grid>

              <Grid item xs={12}><Divider /></Grid>
              <Grid item xs={12}><Typography variant="subtitle1" sx={{ fontWeight: 600 }}>Hero Section</Typography></Grid>
              <Grid item xs={12}><TextField fullWidth label="Hero Headline" value={formData.heroHeadline} onChange={(e) => setFormData({ ...formData, heroHeadline: e.target.value })} /></Grid>
              <Grid item xs={12}><TextField fullWidth label="Hero Subtext" multiline rows={2} value={formData.heroSubtext} onChange={(e) => setFormData({ ...formData, heroSubtext: e.target.value })} /></Grid>
              <Grid item xs={12}><TextField fullWidth label="About / Since text" multiline rows={2} value={formData.aboutText} onChange={(e) => setFormData({ ...formData, aboutText: e.target.value })} /></Grid>

              <Grid item xs={12}><Divider /></Grid>
              <Grid item xs={12}><Typography variant="subtitle1" sx={{ fontWeight: 600 }}>Contact</Typography></Grid>
              <Grid item xs={12} sm={6}><TextField fullWidth label="Phone 1" value={formData.phone1} onChange={(e) => setFormData({ ...formData, phone1: e.target.value })} /></Grid>
              <Grid item xs={12} sm={6}><TextField fullWidth label="Phone 2 (optional)" value={formData.phone2} onChange={(e) => setFormData({ ...formData, phone2: e.target.value })} /></Grid>
              <Grid item xs={12} sm={6}><TextField fullWidth label="Email" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} /></Grid>
              <Grid item xs={12} sm={6}><TextField fullWidth label="Working Hours" value={formData.workingHours} onChange={(e) => setFormData({ ...formData, workingHours: e.target.value })} /></Grid>
              <Grid item xs={12}><TextField fullWidth label="Address" multiline rows={2} value={formData.address} onChange={(e) => setFormData({ ...formData, address: e.target.value })} /></Grid>

              <Grid item xs={12}><Divider /></Grid>
              <Grid item xs={12}><Typography variant="subtitle1" sx={{ fontWeight: 600 }}>Footer</Typography></Grid>
              <Grid item xs={12}><TextField fullWidth label="Footer Tagline" multiline rows={2} value={formData.footerTagline} onChange={(e) => setFormData({ ...formData, footerTagline: e.target.value })} /></Grid>

              <Grid item xs={12}><Divider /></Grid>
              <Grid item xs={12}><Typography variant="subtitle1" sx={{ fontWeight: 600 }}>Sections</Typography></Grid>
              <Grid item xs={12} sm={6}>
                <FormControlLabel
                  control={<Switch checked={formData.showDoctorsSection} onChange={(e) => setFormData({ ...formData, showDoctorsSection: e.target.checked })} />}
                  label='Show "Our Doctors" section (live list of Doctor-role staff)'
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControlLabel
                  control={<Switch checked={formData.showDemoBanner} onChange={(e) => setFormData({ ...formData, showDemoBanner: e.target.checked })} />}
                  label="Show demo banner ribbon"
                />
              </Grid>
              {formData.showDemoBanner && (
                <Grid item xs={12}><TextField fullWidth label="Demo Banner Text" value={formData.demoBannerText} onChange={(e) => setFormData({ ...formData, demoBannerText: e.target.value })} placeholder="This is a demo of the Hospital Management System." /></Grid>
              )}

              <Grid item xs={12}><Divider /></Grid>
              <Grid item xs={12}>
                <RepeatableListEditor
                  title="Stats (hero strip)"
                  items={stats}
                  setItems={setStats}
                  addLabel="Add Stat"
                  fields={[{ name: 'value', label: 'Value', type: 'text' }, { name: 'label', label: 'Label', type: 'text' }]}
                />
              </Grid>
              <Grid item xs={12}><Divider /></Grid>
              <Grid item xs={12}>
                <RepeatableListEditor
                  title="Specialities"
                  items={specialities}
                  setItems={setSpecialities}
                  addLabel="Add Speciality"
                  fields={[{ name: 'title', label: 'Title', type: 'text' }, { name: 'icon', label: 'Icon', type: 'icon' }, { name: 'color', label: 'Color', type: 'color' }]}
                />
              </Grid>
              <Grid item xs={12}><Divider /></Grid>
              <Grid item xs={12}>
                <RepeatableListEditor
                  title="Services"
                  items={services}
                  setItems={setServices}
                  addLabel="Add Service"
                  fields={[
                    { name: 'title', label: 'Title', type: 'text' },
                    { name: 'description', label: 'Description', type: 'textarea' },
                    { name: 'icon', label: 'Icon', type: 'icon' },
                    { name: 'color', label: 'Color', type: 'color' },
                  ]}
                />
              </Grid>
              <Grid item xs={12}><Divider /></Grid>
              <Grid item xs={12}>
                <RepeatableListEditor
                  title="Why Choose Us"
                  items={whyUs}
                  setItems={setWhyUs}
                  addLabel="Add Item"
                  fields={[{ name: 'title', label: 'Title', type: 'text' }, { name: 'desc', label: 'Description', type: 'textarea' }, { name: 'icon', label: 'Icon', type: 'icon' }]}
                />
              </Grid>

              <Grid item xs={12}><Button type="submit" fullWidth variant="contained" size="large" startIcon={<Save />}>Save Settings</Button></Grid>
            </Grid>
          </form>
        </Paper>
      </Container>
    </div>
  );
};

export default HomePageSettingsManagement;
